'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceCardProps {
  title: string;
  description: string;
  price: string;
  image: string;
  alt: string;
  category: 'health' | 'community';
  location: string[];
  features: string[];
  onContact: (e) => void;
  onWhatsApp: (e) => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  title,
  description,
  price,
  image,
  alt,
  category,
  location,
  features,
  onContact,
  onWhatsApp,
}) => {
  const categoryColors = {
    health: 'bg-primary/10 text-primary',
    community: 'bg-secondary/10 text-secondary',
  };

  return (
    <div className="bg-card rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300 overflow-hidden group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[category]}`}>
            {category === 'health' ? 'Health Service' : 'Community Support'}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-heading font-bold text-foreground mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{description}</p>

        <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Relief Rate</p>
            <p className="text-2xl font-heading font-bold text-primary">{price}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="MapPinIcon" size={16} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{location.length} locations</span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {features.slice(0, 3).map((feature, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Icon name="CheckCircleIcon" size={16} className="text-secondary mt-0.5 flex-shrink-0" />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>

        <div className="flex space-x-2">
          <button
            onClick={onContact}
            className="flex-1 px-4 py-2.5 rounded-lg font-heading font-semibold text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <Icon name="PhoneIcon" size={16} />
            <span>Call Now</span>
          </button>
          <button
            onClick={onWhatsApp}
            className="flex-1 px-4 py-2.5 rounded-lg font-heading font-semibold text-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} />
            <span>WhatsApp</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;